// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Style imports:
import './App.scss';
// Component imports:
import NavigationBar from './components/navbar/NavigationBar';
import SigtiHome from './components/shares/SigtiHome';
import Login from './components/login/Login';
import LoginControlCenter from './components/ControlCenters/LoginControlCenter';
import AlertsAll from './components/alerts/AlertsAll';
import BookmarksAll from './components/bookmarks/BookmarksAll';
import AlertConfig from './components/alertconfig/AlertConfig';
import AvailableReports from './components/reports/AvailableReports';
import SingleReport from './components/reports/SingleReport';
import ToastControlCenter from './components/ControlCenters/ToastControlCenter';
import SigtiRangeHistoryWrapper from './components/sigti/history/SigtiRangeHistory';
// Service imports:
import { getLastTradingDay, turnDateToSimpleTime } from './services/utils';
import { isUserLoggedIn } from './services/loginUtils';
// Action imports:
import { setCurrentTradingDay, setDayRange } from './redux-store/actions/dashboardActions';
import { setSelectedStatus } from './redux-store/actions/alertActions';
// Type imports:
import { RootState } from './redux-store/reducers';
import { AlertStatus } from './types/api/AlertTypes';
import SigtiRangeRoute from './components/sigti/SigtiRangeRoute';
import OrderPageRoute from './components/order/OrderPageRoute';
import TradePageRoute from './components/trade/TradePageRoute';

type ReduxProps = ConnectedProps<typeof connector>;
type Props = ReduxProps;

const SingleReportWrapper = () => {
    const { reportid } = useParams();
    return <SingleReport reportId={parseInt(reportid || '0')} />;
};

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
    const isLoggedIn = useSelector((state: RootState) => {
        const { isLoggedIn } = isUserLoggedIn(state.loginReducer);
        return isLoggedIn;
    });
    const location = useLocation();
    return isLoggedIn
        ? element
        : <Navigate to="/login" state={{ from: location }} replace />;
};

class App extends React.Component<Props> {
    componentDidMount() {
        console.log(process.env.REACT_APP_NODE_ENV)
        console.log(process.env.REACT_APP_VERSION)
        this.findAndSetQueryVariables();
        window.onpopstate = () => {
            this.findAndSetQueryVariables();
        }
    }

    setTradingDayControler = (loginUserName: string, urlSearchParams: URLSearchParams) => {
        const { setCurrentTradingDay } = this.props;

        const tradingDay = urlSearchParams.get('tradingDay');
        if (typeof tradingDay === 'string') {
            let tradingDayDate = new Date(tradingDay);
            const lastTradingDay = getLastTradingDay();
            // If faulty date or larger than last trading day, change to last trading day
            const currentTradingDay = (isNaN(tradingDayDate.getTime()))
                ? lastTradingDay
                : turnDateToSimpleTime(tradingDayDate);

            if (
                tradingDayDate > new Date(lastTradingDay)
                //Exception for kodi users.
                && !(loginUserName?.includes('@kodi.is'))
            ) {
                urlSearchParams.set('tradingDay', lastTradingDay);
                window.location.href = window.location.origin + window.location.pathname + '?' + urlSearchParams.toString();
            }
            setCurrentTradingDay(currentTradingDay, true);
        }
    }

    setSelectedStatusControler = (urlSearchParams: URLSearchParams) => {
        const { setSelectedStatus } = this.props;

        const status = urlSearchParams.get('status');
        if (typeof status === 'string') {
            // TODO: type guard statuses variables. Don't hard cast to AlertStatus.
            setSelectedStatus(status as AlertStatus, true);
        } else {
            setSelectedStatus(null, true);
        }
    }

    setDayRangeControler = (urlSearchParams: URLSearchParams) => {
        const { setDayRange } = this.props;

        const fromDate = urlSearchParams.get('fromDate');
        const toDate = urlSearchParams.get('toDate');

        const fromDateCorrected = (typeof fromDate === 'string' && !isNaN(new Date(fromDate).getTime()))
            ? turnDateToSimpleTime(new Date(fromDate))
            : null;
        const toDateCorrected = (typeof toDate === 'string' && !isNaN(new Date(toDate).getTime()))
            ? turnDateToSimpleTime(new Date(toDate))
            : null;

        const lastTradingDay = getLastTradingDay();
        const lastTradingDayDate = new Date(lastTradingDay);
        // If both from and to date are more than last trading day.
        if (
            (new Date(fromDateCorrected || 0) > lastTradingDayDate)
            && (new Date(toDateCorrected || 0) > lastTradingDayDate)
        ) {
            urlSearchParams.set('fromDate', lastTradingDay);
            urlSearchParams.set('toDate', lastTradingDay);
            window.location.href = window.location.origin + window.location.pathname + '?' + urlSearchParams.toString();
            return;
        }
        // If from date is more than last trading day.
        if (new Date(fromDateCorrected || 0) > lastTradingDayDate) {
            urlSearchParams.set('fromDate', lastTradingDay);
            window.location.href = window.location.origin + window.location.pathname + '?' + urlSearchParams.toString();
            return;
        }
        // If to date is more than last trading day.
        if (new Date(toDateCorrected || 0) > lastTradingDayDate) {
            urlSearchParams.set('toDate', lastTradingDay);
            window.location.href = window.location.origin + window.location.pathname + '?' + urlSearchParams.toString();
            return;
        }

        // All checks complete. Set the dates.
        setDayRange(fromDateCorrected || toDateCorrected || lastTradingDay, toDateCorrected || lastTradingDay, true);
    }

    findAndSetQueryVariables = () => {
        const { loginUserName } = this.props;

        const queryVariablesPerPath: {
            [key: string]: string[]
        } = {
            'shares': ['tradingDay'],
            'alertsall': ['status', 'dayRange'],
            'sigti': ['tradingDay'],
            'report': ['dayRange'],
            'allbookmarks': ['tradingDay']
        };

        // Set current trading day from query.
        const query = window.location.search;
        const urlSearchParams = new URLSearchParams(query);

        const path = window.location.pathname.split('/')[1];
        const variablesToRetrieveFromQuery = queryVariablesPerPath[path] || [];

        for (let queryVariable of variablesToRetrieveFromQuery) {
            switch (queryVariable) {
                case 'tradingDay': {
                    this.setTradingDayControler(loginUserName, urlSearchParams);
                    break;
                }
                case 'status': {
                    this.setSelectedStatusControler(urlSearchParams);
                    break;
                }
                case 'dayRange': {
                    this.setDayRangeControler(urlSearchParams);
                    break;
                }
            }
        }
    }

    // Use this function if using landing page. 
    // displayNavigationBar = () => {
    //     const onLandingPage = (window.location.pathname === '/')
    //     if (!onLandingPage) {
    //         return (
    //             <NavigationBar />
    //         );
    //     }
    //     return null;
    // }

    requiresLogin = (component: JSX.Element) => {
        if (this.props.isLoggedIn) {
            return component;
        } else {
            return <Navigate to='/' />;
        }
    }

    render() {
        const { isLoggedIn } = this.props;

        return (
            <div id='App'>
                {/* Use this function if using landing page. */}
                {/* {this.displayNavigationBar()} */}
                <NavigationBar />
                <Routes>
                    {/* ===================== Alert Config ===================== */}
                    {/* Alert Config (activate/deactivate alerts) */}
                    <Route path='/alerts' element={<ProtectedRoute element={<AlertConfig />} />} />

                    {/* ===================== Navigation Bar ===================== */}
                    {/* Bookmarks based on navbar */}
                    <Route path='/allbookmarks' element={<ProtectedRoute element={<BookmarksAll />} />} />
                    {/* Alerts based on navbar */}
                    <Route path='/alertsall' element={<ProtectedRoute element={<AlertsAll />} />} />
                    {/* Owerview based on navbar */}
                    <Route path='/shares' element={<ProtectedRoute element={<SigtiHome />} />} />
                    {/* Reports based on navbar */}
                    <Route path='/reports' element={<ProtectedRoute element={<AvailableReports />} />} />

                    {/* ===================== Info About Item ===================== */}
                    {/* Info about single report */}
                    <Route path='/report/:reportid' element={<ProtectedRoute element={<SingleReportWrapper />} />} />

                    {/* ===================== Sigti ===================== */}
                    {/* Info about single Symbol History Over many Days */}
                    <Route path='/sigti/:symbol/history' element={<ProtectedRoute element={<SigtiRangeHistoryWrapper />} />} />
                    {/* Info about single Symbol */}
                    <Route path='/sigti/:symbol' element={<ProtectedRoute element={<SigtiRangeRoute />} />} />

                    {/* ===================== Order Detail ===================== */}
                    {/* Order detail page by symbol and order ID */}
                    <Route path='/order/:symbol/:orderId' element={<ProtectedRoute element={<OrderPageRoute />} />} />

                    {/* ===================== Trade Detail ===================== */}
                    {/* Trade detail page by symbol and trade ID */}
                    <Route path='/trade/:symbol/:tradeId' element={<ProtectedRoute element={<TradePageRoute />} />} />

                    {/* ===================== Login ===================== */}
                    <Route path='/login' element={<Login />} />

                    {/* ===================== (Used for Redirect!) ===================== */}
                    <Route path='/' element={
                        isLoggedIn
                            ? <Navigate to='/shares' />
                            : <Navigate to='/login' />
                    } />

                    {/* ===================== Fallback ===================== */}
                </Routes>
                {/* Control centers */}
                <LoginControlCenter />
                <ToastControlCenter />
            </div>
        )
    }
}

function mapStateToProps({ loginReducer }: RootState) {
    const { isLoggedIn } = isUserLoggedIn(loginReducer);
    const { loginUserName } = loginReducer;
    return {
        isLoggedIn,
        loginUserName,
    }
}

const connector = connect(mapStateToProps, {
    setCurrentTradingDay,
    setDayRange,
    setSelectedStatus
});
export default connector(App);